<template>
  <div>
    <!-- Add NEW Promo Code Button -->
    <v-btn color="purple" dark class="mb-2" @click="showNewPromoDialog = true">
      New Promo Code
    </v-btn>

    <!-- Promo Codes Table -->
    <v-alert type="error" v-if="errorMsg" class="mt-2 mb-2">
      {{ errorMsg }}
    </v-alert>

    <v-simple-table class="elevated-table mb-2">
      <thead>
        <tr>
          <th @click="sortTable('id')" class="sortable">
            ID
            <v-icon small>{{ getSortIcon('id') }}</v-icon>
          </th>
          <th @click="sortTable('code')" class="sortable">
            Code
            <v-icon small>{{ getSortIcon('code') }}</v-icon>
          </th>
          <th @click="sortTable('discount')" class="sortable">
            Discount (%)
            <v-icon small>{{ getSortIcon('discount') }}</v-icon>
          </th>
          <th @click="sortTable('created_at')" class="sortable">
            Created At
            <v-icon small>{{ getSortIcon('created_at') }}</v-icon>
          </th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="promo in paginatedPromocodes" :key="promo.id">
          <td>{{ promo.id }}</td>
          <td>{{ promo.code }}</td>
          <td>{{ promo.discount }}</td>
          <td>{{ promo.created_at }}</td>
          <td>
            <v-btn color="red" dark @click="confirmDeletePromo(promo)">
              Delete
            </v-btn>
          </td>
        </tr>
        <tr v-if="paginatedPromocodes.length === 0">
          <td colspan="5" class="text-center">No active promo codes.</td>
        </tr>
      </tbody>
    </v-simple-table>

    <!-- Pagination -->
    <div v-if="totalPages > 1" class="d-flex justify-center mb-8">
      <v-pagination
        v-model="currentPage"
        :length="totalPages"
        @input="onPageChange"
      ></v-pagination>
    </div>

    <!-- =================== Confirm Delete Promo Dialog =================== -->
    <v-dialog v-model="showDeletePromoDialog" max-width="450">
      <v-card>
        <v-card-title class="text-h5">
          Confirm Deletion
        </v-card-title>
        <v-card-text>
          Are you sure you want to deactivate promo code "{{ promoToDelete?.code }}"?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="grey" @click="showDeletePromoDialog = false">
            Cancel
          </v-btn>
          <v-btn text color="red" @click="deletePromoNow">
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- =================== New Promo Code Dialog =================== -->
    <v-dialog v-model="showNewPromoDialog" max-width="450">
      <v-card>
        <v-card-title>
          <span class="text-h5">New Promo Code</span>
        </v-card-title>
        <v-card-text>
          <v-form ref="newPromoForm" v-model="promoValid">
            <v-text-field
              label="Promo Code"
              v-model="newPromoData.code"
              :rules="[requiredRule]"
              required
            ></v-text-field>
            <v-text-field
              label="Discount (%)"
              type="number"
              v-model="newPromoData.discount"
              :rules="[discountRule]"
              required
            ></v-text-field>
          </v-form>
          <v-alert type="error" v-if="promoErrorMsg" class="mt-2">
            {{ promoErrorMsg }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="showNewPromoDialog = false">
            Cancel
          </v-btn>
          <v-btn color="purple" text @click="createPromoNow" :disabled="!promoValid">
            Create
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import apiService from '@/services/apiService.js';

export default {
  name: "PromoCodes",
  props: {
    token: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      promocodes: [],
      errorMsg: null,

      // Sorting and Pagination
      sortKey: 'id',
      sortDesc: false,
      currentPage: 1,
      itemsPerPage: 10,

      // Dialogs for Deletion
      showDeletePromoDialog: false,
      promoToDelete: null,

      // === NEW PROMO CODE CREATION ===
      showNewPromoDialog: false,
      newPromoData: {
        code: "",
        discount: null
      },
      promoValid: false,
      promoErrorMsg: null
    };
  },
  computed: {
    // Validation rules
    requiredRule() {
      return v => !!v || "Required field";
    },
    discountRule() {
      return v => (v !== null && v >= 0 && v <= 100) || "Discount must be between 0 and 100";
    },

    // Sorted and Paginated Promos
    sortedPromocodes() {
      return this.sortData(this.promocodes, this.sortKey, this.sortDesc);
    },
    paginatedPromocodes() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.sortedPromocodes.slice(start, end);
    },
    totalPages() {
      return Math.ceil(this.promocodes.length / this.itemsPerPage);
    },
  },
  methods: {
    // Original fetching + deletion
    fetchPromocodes() {
      this.errorMsg = null;
      apiService.fetchPromocodes(this.token)
        .then(res => {
          this.promocodes = res.data;
        })
        .catch(err => {
          // Emit global error to AdminDashboard OR set local error
          this.$emit('error', err.response?.data?.error || "Failed to fetch promo codes.");
        });
    },
    sortTable(key) {
      if (this.sortKey === key) {
        this.sortDesc = !this.sortDesc;
      } else {
        this.sortKey = key;
        this.sortDesc = false;
      }
    },
    getSortIcon(key) {
      if (this.sortKey === key) {
        return this.sortDesc ? 'mdi-arrow-down' : 'mdi-arrow-up';
      }
      return 'mdi-sort';
    },
    sortData(array, key, desc) {
      return array.slice().sort((a, b) => {
        let result = 0;
        if (key === 'discount') {
          result = Number(a[key]) - Number(b[key]);
        } else if (key === 'created_at') {
          result = new Date(a[key]) - new Date(b[key]);
        } else {
          if (a[key] < b[key]) result = -1;
          if (a[key] > b[key]) result = 1;
        }
        return desc ? -result : result;
      });
    },
    onPageChange() {
      // no-op
    },
    confirmDeletePromo(promo) {
      this.promoToDelete = promo;
      this.showDeletePromoDialog = true;
    },
    deletePromoNow() {
      if (!this.promoToDelete) {
        this.showDeletePromoDialog = false;
        return;
      }
      apiService.deletePromoCode(this.promoToDelete.id, this.token)
        .then(() => {
          this.showDeletePromoDialog = false;
          this.promoToDelete = null;
          this.fetchPromocodes();
        })
        .catch(err => {
          this.$emit('error', err.response?.data?.error || "Failed to delete promo code.");
          this.showDeletePromoDialog = false;
          this.promoToDelete = null;
        });
    },

    // === CREATE NEW PROMO CODE ===
    createPromoNow() {
      // Basic check
      if (!this.newPromoData.code || this.newPromoData.discount === null) {
        this.promoErrorMsg = "All fields are required.";
        return;
      }

      this.promoErrorMsg = null;
      apiService.createPromoCode(this.newPromoData, this.token)
        .then(() => {
          // Close dialog
          this.showNewPromoDialog = false;
          // Reset data
          this.newPromoData = { code: "", discount: null };
          // Refresh
          this.fetchPromocodes();
        })
        .catch(err => {
          this.promoErrorMsg = err.response?.data?.error || "Failed to create promo code.";
        });
    }
  },
  mounted() {
    this.fetchPromocodes();
  }
};
</script>

  <style scoped>
  .elevated-table {
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  }
  
  ::v-deep .elevated-table table {
    border-collapse: separate !important;
    border-spacing: 0 6px !important;
  }
  
  ::v-deep .elevated-table th,
  ::v-deep .elevated-table td {
    padding: 16px 24px !important;
    text-align: left !important;
    vertical-align: middle !important;
  }
  
  .sortable {
    cursor: pointer;
    user-select: none;
  }
  
  .sortable .v-icon {
    vertical-align: middle;
    margin-left: 4px;
  }
  
  .text-center {
    text-align: center;
  }
  
  .mb-2 {
    margin-bottom: 8px;
  }
  
  .mt-2 {
    margin-top: 8px;
  }
  
  .mb-8 {
    margin-bottom: 32px;
  }
  
  .ml-2 {
    margin-left: 8px;
  }
  
  .mr-4 {
    margin-right: 16px;
  }
  
  .mr-2 {
    margin-right: 8px;
  }
  </style>
  